import { getFramework, type IFramework, type TextRenderSnapshot } from '@/framework';
import { TextRenderEvents, type TextRenderInvalidationEventPayload } from '@/framework/services/text-render';
import {
    BaseLayerViewType,
    TextWritingType,
    type ILayerProps,
    type ITextLayerProps,
    type Optional,
} from '@/version-types';
import { Events } from '../../constants';
import type { IEdgelessTextLayer, SVGPreviewRequest, TextLayout } from '../../types';
import { collectTextLayers } from '../../utils/layer';

/**
 * previewCache 的值会在 publish() 中汇总到 previews，再经 getPreviews() 提供给
 * TextLayerSVGPreview 的 useSyncExternalStore()，最终用于渲染图层位置和 SVG 内容。
 */
type Preview = {
    layer: ILayerProps;
    layout: TextLayout;
    svg: SVGSVGElement;
};

export class TextLayerSVGPreviewPlugin {
    private readonly framework: IFramework;

    private readonly previewCache = new Map<string, Preview>();

    private readonly pendingLayers = new Map<string, { layer: ILayerProps; snapshot?: Optional<TextRenderSnapshot> }>();
    private readonly active = new Set<string>();
    private readonly waitingForDrawable = new Set<string>();

    private readonly subscribers = new Set<() => void>();

    private previews: Preview[] = [];
    private animationFrame = 0;

    /**
     * 文本预览不能直接在 requestAnimationFrame 中 flush：宿主 Text Renderer 会同步执行 TextBuffer
     * 排版，部分字形还需要 Canvas measureText。如果这些工作占满 rAF，浏览器就无法及时
     * 绘制本帧的选框和拖拽反馈。这里通过 setTimeout 把 flush 放到后续任务，让浏览器先完成当前帧绘制。
     */
    private flushTimer = 0;

    /**
     * 创建文本图层 SVG 连续交互预览管理器。
     * @param identity - 当前编辑器实例标识。
     */
    constructor(identity: string) {
        this.framework = getFramework(identity);
        const { events, frame, flatLayer } = this.framework.service;

        events.on(Events.SVGPreviewRequest, (request: SVGPreviewRequest) => this.handleRequest(request));

        events.on(Events.DrawableSettled, (layerID: string, ready: boolean) => {
            if (!this.waitingForDrawable.has(layerID)) return;
            this.release(layerID);
            if (!ready) this.framework.service.edgelessCanvas.renderAll();
        });

        events.on(TextRenderEvents.Invalidation, ({ layerId }: TextRenderInvalidationEventPayload) =>
            this.refreshOnInvalidation(layerId),
        );

        frame.onBeforeBaseOperate((layer, type) => {
            if (type === BaseLayerViewType.RESIZE) this.begin(layer);
        });

        frame.onBaseOperate((_layerID, type, args) => {
            if (type === BaseLayerViewType.RESIZE && args.isEnd) this.end();
        });

        flatLayer.onDelete((layerIDs) => layerIDs.forEach((layerID) => this.release(layerID)));
    }

    /**
     * 获取当前已发布的 SVG 预览列表。
     * @returns 当前 SVG 预览列表。
     */
    getPreviews = () => this.previews;

    /**
     * 订阅 SVG 预览列表变化。
     * @param listener - 预览列表变化回调。
     * @returns 取消订阅函数。
     */
    subscribe = (listener: () => void) => {
        this.subscribers.add(listener);
        return () => this.subscribers.delete(listener);
    };

    private schedule(layer: ILayerProps, snapshot?: SVGPreviewRequest['snapshot']) {
        this.pendingLayers.set(layer.id, { layer, snapshot });
        if (this.animationFrame || this.flushTimer) return;

        this.animationFrame = requestAnimationFrame(() => {
            this.animationFrame = 0;
            this.flushTimer = window.setTimeout(() => {
                this.flushTimer = 0;
                this.flush();
            });
        });
    }

    /**
     * 清理连续预览状态。
     *
     * 由插件 `destroy` 钩子在 `editor.init()` 时调用：只清预览缓存与进行中的 flush，
     * 保留 RESIZE 事件监听和 React 订阅。框架里该钩子是文档重载时的 reset，不是真正销毁。
     * @returns void。
     */
    dispose() {
        if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
        if (this.flushTimer) clearTimeout(this.flushTimer);

        this.animationFrame = 0;
        this.flushTimer = 0;
        this.active.forEach((layerID) => this.release(layerID, false));
        this.pendingLayers.clear();
        this.waitingForDrawable.clear();
        this.publish();
    }

    /**
     * 开始连续交互时创建 SVG 预览并隐藏正式渲染层。
     * @param layer - 当前操作的根图层。
     * @returns void。
     */
    private begin(layer: ILayerProps, requestedSnapshot?: SVGPreviewRequest['snapshot']) {
        this.active.forEach((layerID) => this.release(layerID, false));

        collectTextLayers(layer).forEach((item) => {
            const props = item.data as ITextLayerProps;
            if (props.shapeExtension) return;

            const object = this.framework.service.edgelessCanvas.getByLayerId(item.id) as IEdgelessTextLayer | null;
            if (!object) return;

            const isEmpty = props.text === '';
            // 空文本没有 SVG，但仍需接管渲染，避免 dataOnly 更新后露出旧 drawable。
            const resolution =
                !isEmpty && (requestedSnapshot === undefined || item.id !== layer.id) ? object.layout(item) : null;
            // Preview 不回退到旧几何，只有当前 ready snapshot 才能接管正式画面。
            const snapshot = resolution?.status === 'ready' ? resolution.value : (requestedSnapshot ?? null);
            const preview = !isEmpty && snapshot ? object.resolveRuntimeSVGResult(item, snapshot) : null;
            if (!isEmpty && !preview) return;

            this.active.add(item.id);
            this.framework.service.events.emit(Events.SVGPreviewActiveChange, item.id, true);

            // previewActive 只改变对象的渲染跳过状态，不会触发图层更新；主动标脏以隐藏正式 drawable，避免与 SVG 预览重叠。
            this.framework.service.edgelessCanvas.setDirty({ object, onlyUpdateBaseInfo: true });
            if (preview) {
                this.previewCache.set(item.id, {
                    layer: item,
                    layout: preview.layout,
                    svg: preview.svg,
                });
            }
        });

        this.publish();
        if (this.active.size) this.framework.service.edgelessCanvas.renderAll();
    }

    /**
     * 标记连续交互已结束，等待正式 drawable 就绪后回切。
     * @returns void。
     */
    private end() {
        this.active.forEach((layerID) => this.waitingForDrawable.add(layerID));
    }

    /**
     * 处理外部发出的 preview 通知。
     *
     * @param request - 预览请求。
     */
    private handleRequest(request: SVGPreviewRequest) {
        const { phase, layer, snapshot } = request;

        // 这里使用 collectTextLayers 主要因为文本图层 API 支持对 Group 内多个文本图层批量修改
        const layers = collectTextLayers(layer);
        const hasActive = layers.some((item) => this.active.has(item.id));

        if (phase === 'commit') {
            layers.forEach((item) => this.pendingLayers.delete(item.id));
            if (hasActive) this.end();
            return;
        }

        if (phase === 'refresh') {
            layers.forEach((item) => {
                if (!this.active.has(item.id)) return;
                this.schedule(item, item.id === layer.id ? snapshot : undefined);
            });
            return;
        }

        if (!hasActive) {
            this.begin(layer, snapshot);
            return;
        }

        layers.forEach((item) => {
            if (!this.active.has(item.id)) return;
            this.waitingForDrawable.delete(item.id);
            this.schedule(item, item.id === layer.id ? snapshot : undefined);
        });
    }

    /**
     * 宿主 Text Render 结果失效时，刷新本插件持有的活动预览。
     *
     * 只处理「已激活且非编辑态」的预览：Resize / API 连续交互建立的预览遇到字体子集或素材晚到时，
     * 需要用最新几何重建，等价于旧的 `refresh` 请求。编辑态的失效刷新由 Text Editor 用 session
     * 最新工作值发 `update` 独占处理，因此这里遇到编辑态直接跳过，避免与编辑器双发；未激活的图层
     * 也不主动建立预览（与旧 `refresh` 语义一致）。
     *
     * 编辑标记必须从默认 `getLayer(id)` 读取——`getLayer(id, true)` 的 clone hook 会删除
     * `__isOnTextEditing`；schedule 的 payload 仍使用 clone，与旧 `refresh` 传入的工作副本一致。
     *
     * @param layerId - 结果已失效的宿主图层 ID。
     * @returns void。
     */
    private refreshOnInvalidation(layerId: string) {
        if (!this.active.has(layerId)) return;

        const { flatLayer } = this.framework.service;
        if (flatLayer.getLayer(layerId)?.__isOnTextEditing) return;

        const layer = flatLayer.getLayer(layerId, true);
        if (layer) this.schedule(layer);
    }

    /**
     * 在动画帧中消费最新状态并发布 SVG 预览。
     * @returns void。
     */
    private flush() {
        this.pendingLayers.forEach(({ layer, snapshot: preparedSnapshot }, layerID) => {
            const props = layer.data as ITextLayerProps;
            // empty 是成功清除；只有 pending/failed 才保留上一份有效 Preview。
            if (props.text === '') {
                this.previewCache.delete(layerID);
                return;
            }

            const object = this.framework.service.edgelessCanvas.getByLayerId(layerID) as IEdgelessTextLayer | null;
            const resolution = preparedSnapshot === undefined ? object?.layout(layer) : null;

            // 刷新只能提交本轮 ready 几何，pending/failed 保留当前 Preview。
            const snapshot = resolution?.status === 'ready' ? resolution.value : preparedSnapshot;
            if (!object || !snapshot) return;

            const nextLayer = { ...layer };
            // resize 主流程统一提交真实几何；预览只在自身投影中回填内容尺寸。
            if (props.writingMode !== TextWritingType.HORIZONTAL_TB) {
                nextLayer.width = snapshot.contentWidth;
            } else {
                nextLayer.height = snapshot.contentHeight;
            }

            const result = object.resolveRuntimeSVGResult(nextLayer, snapshot);
            if (result) {
                this.previewCache.set(layerID, {
                    layer: nextLayer,
                    layout: result.layout,
                    svg: result.svg,
                });
            }
        });

        this.pendingLayers.clear();
        this.publish();
    }

    /**
     * 释放指定文本图层的 SVG 预览状态。
     * @param layerID - 文本图层 ID。
     * @param publish - 是否立即发布最新预览列表。
     * @returns void。
     */
    private release(layerID: string, publish = true) {
        this.framework.service.events.emit(Events.SVGPreviewActiveChange, layerID, false);

        this.active.delete(layerID);
        this.waitingForDrawable.delete(layerID);
        this.pendingLayers.delete(layerID);
        this.previewCache.delete(layerID);

        if (publish) this.publish();
    }

    /**
     * 发布最新 SVG 预览列表并通知订阅者。
     * @returns void。
     */
    private publish() {
        this.previews = [...this.previewCache.values()];
        this.subscribers.forEach((listener) => listener());
    }
}
